import mixpanel from 'mixpanel-browser';
import { EVENTS } from './events';
import analyticsStorage from './storage';
import syncManager from './sync';
import geoLocationHelper from './geo';
import store from '@/store';

const MIXPANEL_TOKEN = (window?.thinkmd && window?.thinkmd?.mixpanel_token) || process.env.VUE_APP_MIXPANEL_TOKEN;
const IS_DEV = process.env.NODE_ENV !== 'production';

function resolveAnalyticsFlag() {
  if (IS_DEV) {
    try {
      const localOverride = localStorage.getItem('analytics_enabled');
      if (localOverride === 'true') {
        return true;
      }
      if (localOverride === 'false') {
        return false;
      }
    } catch (error) {
      console.warn('[Analytics] Unable to read analytics_enabled from localStorage:', error);
    }
  }

  if (window?.thinkmd && typeof window?.thinkmd?.analytics_enabled === 'boolean') {
    return window?.thinkmd?.analytics_enabled;
  }

  return false;
}

// resolveAnalyticsFlag is called at runtime each time — not stored as a build-time const
function isAnalyticsEnabled() {
  return resolveAnalyticsFlag();
}

let mixpanelInitialized = false;

function ensureMixpanelInitialized() {
  if (mixpanelInitialized) return;
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: IS_DEV,
    track_pageview: true,
    persistence: 'localStorage',
    ignore_dnt: false,
  });

  mixpanelInitialized = true;
}

/**
 * Resolves the user ID from multiple Vuex sources in priority order:
 * preference getter → panel getter → session details
 * @returns {string|number|null}
 */
function resolveUserId() {
  try {
    const prefGetter = store.getters['v4/preference/getResponse'];
    const userId = prefGetter ? prefGetter('user_id', 'number')?.value : null;
    if (userId) return userId;

    const panelGetter = store.getters['v4/panel/getResponse'];
    const userIdFromPanel = panelGetter ? panelGetter('user_id', 'number')?.value : null;
    if (userIdFromPanel) return userIdFromPanel;

    const sessionDetails = store.getters['v4/session/details']?.();
    return sessionDetails?.user?.id || null;
  } catch (e) {
    return null;
  }
}

/**
 * Resolves the panel_name from Vuex route params, then falls back to the URL hash.
 * @returns {string|null}
 */
function resolvePanelName() {
  const routePanelName = store.state.route?.params?.panels_name;
  if (routePanelName) return routePanelName;

  // Fallback: extract from hash route (e.g. /#/past-visits → past-visits)
  const hashPath = window.location.hash?.replace(/^#\/?/, '').split('?')[0];
  return hashPath || null;
}

/**
 * Track custom events to Mixpanel
 * @param {string} event - Event name
 * @param {object} data - Event properties
 */
function track(event, data = {}) {
  ensureMixpanelInitialized();
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  try {
    const payload = {
      ...data,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
    };

    if (store && store.state) {
      if (event && !event.startsWith('$')) {
        const encounterCode = store.state.settings?.code;
        if (encounterCode) {
          payload.encounter_code = encounterCode;
        }
      }

      const language = store.state.v4?.language?.selected;
      if (language) {
        payload.language = language;
      }

      const hrId = store.state.v4?.registration?.hrId;
      if (hrId) {
        payload.hr_id = hrId;
      }

      if (!payload.panel_name) {
        const panelName = resolvePanelName();
        if (panelName) payload.panel_name = panelName;
      }

      if (!payload.user_id) {
        const userId = resolveUserId();
        if (userId) payload.user_id = userId;
      }
    }

    mixpanel.track(event, payload);
  } catch (error) {
    if (IS_DEV) {
      console.error('[Analytics] Error tracking event:', error);
    }
  }
}

/**
 * Get element metadata for click tracking
 * @param {HTMLElement} element - The clicked element
 * @returns {object} Element metadata
 */
function getElementMetadata(element) {
  const metadata = {
    tag_name: element.tagName.toLowerCase(),
    element_id: element.id || null,
    element_classes: element.className || null,
    element_text: element.textContent?.trim().substring(0, 100) || null,
  };

  if (element.tagName === 'BUTTON') {
    metadata.button_type = element.type || 'button';
  } else if (element.tagName === 'A') {
    metadata.link_href = element.href || null;
  }

  if (element.__vue__) {
    metadata.vue_component = element.__vue__.$options.name || element.__vue__.$options._componentTag || null;
  }

  const dataAttrs = {};
  Array.from(element.attributes).forEach(attr => {
    if (attr.name.startsWith('data-')) {
      dataAttrs[attr.name] = attr.value;
    }
  });
  if (Object.keys(dataAttrs).length > 0) {
    metadata.data_attributes = dataAttrs;
  }

  return metadata;
}

let clickTrackingInitialized = false;
const recentEvents = new Set();
let currentUserId = null;
let currentVisitId = null;
const STORAGE_KEY_USER_ID = 'analytics_user_id';

function setupGlobalClickTracking() {
  if (clickTrackingInitialized) return;
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  clickTrackingInitialized = true;

  document.addEventListener('click', (event) => {
    const element = event.target;
    const clickableElement = element.closest('button, a, [role="button"], [onclick]');

    if (clickableElement) {
      if (clickableElement.hasAttribute('data-analytics-skip')) {
        return;
      }

      const elementId = clickableElement.id || '';
      const elementClass = clickableElement.className || '';
      const elementText = (clickableElement.textContent || '').trim().substring(0, 20);
      const dedupKey = `${clickableElement.tagName}_${elementId}_${elementClass}_${elementText}`;
      
      if (recentEvents.has(dedupKey)) {
        return;
      }
      
      recentEvents.add(dedupKey);
      setTimeout(() => recentEvents.delete(dedupKey), 500);

      const metadata = getElementMetadata(clickableElement);
      track(EVENTS.CLICK_EVENT, metadata);
    }
  });
}

function identifyUser(userId, properties = {}) {
  ensureMixpanelInitialized();
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  try {
    mixpanel.identify(userId.toString());
    mixpanel.people.set({
      $name: properties.name || null,
      user_type: properties.user_type || null,
      registration_id: properties.registration_id || null,
      hr_id: properties.hr_id || null,
      device_id: properties.device_id || null,
      last_login: new Date().toISOString(),
      ...properties
    });
  } catch (error) {
    if (IS_DEV) {
      console.error('[Analytics] Error identifying user:', error);
    }
  }
}

/**
 * Get or generate device ID
 * @returns {string} Device ID
 */
function getDeviceId() {
  const STORAGE_KEY = 'mixpanel_device_id';
  let deviceId = localStorage.getItem(STORAGE_KEY);

  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem(STORAGE_KEY, deviceId);
  }

  return deviceId;
}

function setUserId(userId) {
  currentUserId = userId;
  localStorage.setItem(STORAGE_KEY_USER_ID, userId);
}

function setVisitId(visitId) {
  currentVisitId = visitId;
}

async function getSyncStats() {
  return syncManager.getStats();
}

function triggerSync() {
  syncManager.syncNow();
}

const AnalyticsService = {
  install(Vue) {
    Vue.prototype.$track = track;
    Vue.prototype.$identifyUser = identifyUser;
    Vue.prototype.$setUserId = setUserId;
    Vue.prototype.$setVisitId = setVisitId;
    Vue.prototype.$getSyncStats = getSyncStats;
    Vue.prototype.$triggerSync = triggerSync;
    Vue.prototype.$getDeviceId = getDeviceId;
    Vue.prototype.$analyticsEvents = EVENTS;
    Vue.prototype.$isAnalyticsEnabled = isAnalyticsEnabled;
    Vue.mixin({
      mounted() {
        if (this === this.$root) {
          ensureMixpanelInitialized();
          if (IS_DEV) {
            if (!MIXPANEL_TOKEN) {
              console.warn('[Analytics] Mixpanel token not found. Analytics tracking is disabled.');
            }
            if (!isAnalyticsEnabled()) {
              console.info('[Analytics] Analytics tracking is DISABLED by feature flag.');
            }
          }
          setupGlobalClickTracking();
        }
      }
    });
  }
};

export { track, isAnalyticsEnabled };
export default AnalyticsService;
